import React, { useState, useEffect } from 'react';
import { apiFetch } from './utils/apiUtils';
import { Card, CardHeader, CardContent, Typography, Box, CircularProgress, Divider, Grid } from '@mui/material';

const TodaysTotals = ({ refreshKey }) => {
  const [todaysData, setTodaysData] = useState(null);
  const [mtdData, setMtdData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Fetch today's data
        console.log('Fetching today\'s data...');
        const todaysResponse = await apiFetch('/api/reports/todays-totals');
        if (!todaysResponse.ok) {
          throw new Error(`Failed to fetch today's data: ${todaysResponse.status} ${todaysResponse.statusText}`);
        }
        const todaysDataResult = await todaysResponse.json();
        console.log('Received today\'s data from API:', todaysDataResult);
        setTodaysData(todaysDataResult);
        
        // Calculate dates for MTD (Month-To-Date)
        const today = new Date();
        const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
        const yesterday = new Date(today);
        yesterday.setDate(today.getDate() - 1);
        
        // Format dates as YYYY-MM-DD
        const fromDate = firstDayOfMonth.toISOString().split('T')[0];
        const toDate = yesterday.toISOString().split('T')[0];
        
        console.log(`Fetching MTD data from ${fromDate} to ${toDate}...`);
        
        // Fetch MTD data using groomer-performance endpoint
        const mtdResponse = await apiFetch(`/api/reports/groomer-performance?from=${fromDate}&to=${toDate}`);
        if (!mtdResponse.ok) {
          throw new Error(`Failed to fetch MTD data: ${mtdResponse.status} ${mtdResponse.statusText}`);
        }
        const mtdDataResult = await mtdResponse.json();
        console.log('Received MTD data from API:', mtdDataResult);
        setMtdData(mtdDataResult);
      } catch (err) {
        console.error('Error fetching data:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [refreshKey]); // Add refreshKey to the dependency array

  // Calculate MTD metrics
  const calculateMtdMetrics = () => {
    if (!mtdData || !Array.isArray(mtdData)) {
      return {
        mtdTotal: 0,
        mtdAvgDaily: 0,
        projectedMonthTotal: 0
      };
    }
    
    // Calculate MTD total (sum of totalCollectedPriceToday from all groomers)
    const mtdTotal = mtdData.reduce((total, groomer) => {
      return total + (groomer.numCompletedPetsToday || 0);
    }, 0);
    
    // Calculate days in the month
    const today = new Date();
    const daysInMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0).getDate();
    
    // Calculate number of days so far this month (excluding today)
    const dayOfMonth = today.getDate() - 1; // Subtract 1 to exclude today
    const daysElapsed = dayOfMonth > 0 ? dayOfMonth : 1; // Ensure we don't divide by zero
    
    // Calculate MTD average daily services
    const mtdAvgDaily = mtdTotal / daysElapsed;
    
    // Calculate projected month total services
    const projectedMonthTotal = Math.round(mtdAvgDaily * daysInMonth);
    
    return {
      mtdTotal,
      mtdAvgDaily: Math.round(mtdAvgDaily * 10) / 10, // Round to 1 decimal place
      projectedMonthTotal,
      daysElapsed,
      daysInMonth
    };
  };
  
  const mtdMetrics = calculateMtdMetrics();

  if (loading) {
    return (
      <Card elevation={3} sx={{ height: '400px', ml: 2 }}>
        <CardHeader
          title="Today's Booking Results"
          titleTypographyProps={{ 
            align: 'center', 
            variant: 'h6',
            sx: { fontSize: '1rem', fontWeight: 'bold', color: 'white' }
          }}
          sx={{ bgcolor: '#1976d2' }}
        />
        <CardContent sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '320px' }}>
          <CircularProgress />
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card elevation={3} sx={{ height: '400px', ml: 2 }}>
        <CardHeader
          title="Today's Booking Results"
          titleTypographyProps={{ 
            align: 'center', 
            variant: 'h6',
            sx: { fontSize: '1rem', fontWeight: 'bold', color: 'white' }
          }}
          sx={{ bgcolor: '#1976d2' }}
        />
        <CardContent sx={{ height: '320px' }}>
          <Typography color="error" align="center">{error}</Typography>
        </CardContent>
      </Card>
    );
  }

  const totalsData = [
    { label: "Appts Booked by Staff", value: todaysData?.non_groomer_num_appts_booked },
    { label: "Pets Booked by Staff", value: todaysData?.non_groomer_num_pets_booked },
    { label: "Groomer Appts Booked", value: todaysData?.groomer_num_appts_booked },
    { label: "Groomer Pets Booked", value: todaysData?.groomer_num_pets_booked },
    { label: "Total Appts Booked", value: todaysData?.total_appt_booked },
    { label: "Total Pets Booked", value: todaysData?.total_pets_booked },
    { label: "Avg 7 Day Bookings", value: todaysData?.avg_7_day_bookings },
    { label: "Cancellations", value: todaysData?.cancellations },
  ];

  return (
    <Card elevation={3} sx={{ height: '365px', ml: 2 }}>
      <CardHeader
        title="Today's Booking Results"
        titleTypographyProps={{ 
          align: 'center', 
          variant: 'h6',
          sx: { fontSize: '1rem', fontWeight: 'bold', color: 'white' }
        }}
        sx={{ bgcolor: '#1976d2' }}
      />
      <CardContent sx={{ py: 1, px: 2, height: '330px' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {totalsData.map((item, index) => (
            <Box key={index} sx={{ display: 'flex', justifyContent: 'space-between', borderBottom: index < totalsData.length - 1 ? '1px solid #eee' : 'none', py: 0.5 }}>
              <Typography variant="body2" sx={{ fontWeight: 'medium', fontSize: '0.9rem' }}>
                {item.label}:
              </Typography>
              <Typography variant="body2" color="primary" sx={{ fontWeight: 'bold', fontSize: '0.9rem' }}>
                {item.value !== undefined ? item.value : 'N/A'}
              </Typography>
            </Box>
          ))}
        </Box>
      </CardContent>
    </Card>
  );
};

export default TodaysTotals;